import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { UserService } from '../../../service/user.service';
import { AuthService } from '../../../service/auth.service';
import { PrimengImports } from '../../../shared/primeng-imports';
import { MessageService } from 'primeng/api';
import { TokenIdService } from '../../../service/token-id.service';


@Component({
  selector: 'app-settings-tab',
  imports: [CommonModule, LucideIconsModule, PrimengImports, FormsModule, ReactiveFormsModule],
  templateUrl: './settings-tab.component.html',
  styleUrl: './settings-tab.component.css',
  providers: [MessageService]
})

export class SettingsTabComponent implements OnInit {
  profileForm!: FormGroup;
  profileLoading = false;
  user: any = null;
  showEmailChangeDialog = false;
  newEmail = '';
  emailChangeLoading = false;
  emailChangeError = '';

  investmentGoalOptions = [
    { value: '', label: 'Select your investment focus' },
    { value: 'real-estate', label: 'Real Estate Portfolio' },
    { value: 'clean-energy', label: 'Clean Energy (LNG)' },
    { value: 'ai-tech', label: 'Artificial Intelligence' },
    { value: 'agriculture', label: 'Agriculture Fund' },
    { value: 'diversified', label: 'Diversified Portfolio' }
  ];

  constructor(private fb: FormBuilder, private userService: UserService, private authService: AuthService, private messageService: MessageService, private tokenIdService: TokenIdService) { }

  ngOnInit(): void {
    this.initializeForm();
    this.loadUserProfile();
  }

  private initializeForm(): void {
    this.profileForm = this.fb.group({
      userId: [null],
      firstName: [null, [Validators.required]],
      lastName: [null, [Validators.required]],
      email: [null],
      phone: [null, [Validators.required]],
      investmentGoals: [null, [Validators.required]],
      lastLogin: [null],
      createdAt: [null],
      fullName: [null]
    });
  }

  private loadUserProfile(): void {
    this.profileLoading = true;
    const token = this.userService.getAuthToken();
    const decoded = this.tokenIdService.decodeToken(token || '');
    const userId = decoded?.userId;
    if (!userId) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'User not authenticated.', life: 3000 });
      this.profileLoading = false;
      return;
    }
    this.userService.getProfile(userId).subscribe({
      next: (user: any) => {
        this.user = user;
        this.profileForm.patchValue({
          userId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone,
          investmentGoals: user.investmentGoals,
          lastLogin: user.lastLogin,
          createdAt: user.createdAt,
          fullName: user.fullName
        });
        this.profileLoading = false;
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to load profile.', life: 3000 });
        this.profileLoading = false;
      }
    });
  }

  onProfileUpdate(): void {
    if (this.profileForm.invalid) {
      return;
    }
    this.profileLoading = true;
    try {
      const formValue = this.profileForm.value;
      this.userService.updateProfile(formValue).subscribe({
        next: (user: any) => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Profile updated successfully!', life: 3000 });
          this.loadUserProfile();
          this.profileLoading = false;
        },
        error: (err: any) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to load profile.', life: 3000 });
          this.profileLoading = false;
        }
      });

    } catch (err: any) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to update profile. Please try again.', life: 3000 });
    } finally {
      this.profileLoading = false;
    }
  }

  requestEmailChange(): void {
    this.emailChangeLoading = true;
    this.emailChangeError = '';
    this.authService.requestEmailChange(this.newEmail).subscribe({
      next: (res: any) => {
        this.showEmailChangeDialog = false;
        this.emailChangeLoading = false;
        this.messageService.add({ 
          severity: 'success', 
          summary: 'Verification Sent', 
          detail: 'A verification link has been sent to your new email address. Please check your inbox.', 
          life: 5000 
        });
        this.newEmail = '';
      },
      error: (err: any) => {
        this.emailChangeLoading = false;
        this.emailChangeError = err?.error?.message || 'Failed to request email change. Please ensure the email is not already in use and try again.';
      }
    });
  }

  getFormattedUserId(): string {
    return this.user?.id ? `INV-${this.user.id.padStart(6, '0')}` : '';
  }

  getFormattedLastLogin(): string {
    return this.user?.lastLogin ? new Date(this.user.lastLogin).toLocaleDateString() : 'N/A';
  }
}
